import React, { useState } from 'react';
import { HugeiconsIcon } from "@hugeicons/react";
import { Shield01Icon, EyeIcon, LockIcon, UserIcon, MailIcon, AiPhoneIcon } from "@hugeicons/core-free-icons";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { SortOption } from "../components/search/searchUtils";
export default function PrivacyPolicy() {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>('name-asc');
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [sortMenuOpen, setSortMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[var(--background)]">
      <Header 
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        sortBy={sortBy}
        setSortBy={setSortBy}
        filterMenuOpen={filterMenuOpen}
        setFilterMenuOpen={setFilterMenuOpen}
        sortMenuOpen={sortMenuOpen}
        setSortMenuOpen={setSortMenuOpen}
      />
      
      {/* Hero Section */}
      <section className="w-full bg-white">
        <div className="container py-0">
          <div className="text-center pt-16 pb-10 md:pt-[120px] md:pb-24">
            <div className="mb-4">
              <h1 className="text-4xl text-[var(--foreground)]">Privacy Policy</h1>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="w-full py-10 md:py-24 bg-gray-50">
        <div className="container mx-auto max-w-4xl">
          <div className="space-y-8">
            
            {/* Last Updated */}
            <div className="mb-8">
              <p className="bodysmall text-[var(--dispensary-gray-500)]">
                Last updated: {new Date().toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            </div>

            {/* Information We Collect */}
            <div className="md:mb-8">
              <h4 className="h4 mb-2">
                Information We Collect
              </h4>
              <div className="space-y-4">
                <p className="bodysmall md:bodymedium text-[var(--dispensary-gray-600)]">
                  We collect information you provide directly to us, such as when you create an account, 
                  place an order, or contact our support team. This may include:
                </p>
                <ul className="list-disc list-inside space-y-2 bodysmall md:bodymedium text-[var(--dispensary-gray-600)] ml-4">
                  <li>Name, email address, and phone number</li>
                  <li>Delivery address and billing information</li>
                  <li>Prescription information and medical history</li>
                  <li>Payment method details</li>
                  <li>Communication preferences</li>
                </ul>
              </div>
            </div>

            {/* How We Use Your Information */}
            <div className="md:mb-8">
              <h4 className="h4 mb-2">
                How We Use Your Information
              </h4>
              <div className="space-y-4">
                <p className="bodysmall md:bodymedium text-[var(--dispensary-gray-600)]">
                  We use the information we collect to:
                </p>
                <ul className="list-disc list-inside space-y-2 bodysmall md:bodymedium text-[var(--dispensary-gray-600)] ml-4">
                  <li>Process and fulfill your orders</li>
                  <li>Verify prescriptions and ensure medication safety</li>
                  <li>Provide customer support and respond to inquiries</li>
                  <li>Send order confirmations and delivery updates</li>
                  <li>Improve our services and develop new features</li>
                  <li>Comply with legal and regulatory requirements</li>
                </ul>
              </div>
            </div>

            {/* Information Sharing */}
            <div className="md:mb-8">
              <h4 className="h4 mb-2">
                Information Sharing
              </h4>
              <div className="space-y-4">
                <p className="bodysmall md:bodymedium text-[var(--dispensary-gray-600)]">
                  We do not sell, trade, or otherwise transfer your personal information to third parties 
                  except in the following circumstances:
                </p>
                <ul className="list-disc list-inside space-y-2 bodysmall md:bodymedium text-[var(--dispensary-gray-600)] ml-4">
                  <li>With your explicit consent</li>
                  <li>To trusted third-party service providers who assist in operating our website</li>
                  <li>To comply with legal obligations or protect our rights</li>
                  <li>In connection with a business transfer or merger</li>
                </ul>
              </div>
            </div>

            {/* Data Security */}
            <div className="md:mb-8">
              <h4 className="h4 mb-2">
                Data Security
              </h4>
              <div className="space-y-4">
                <p className="bodysmall md:bodymedium text-[var(--dispensary-gray-600)]">
                  We implement appropriate security measures to protect your personal information against 
                  unauthorized access, alteration, disclosure, or destruction. These measures include:
                </p>
                <ul className="list-disc list-inside space-y-2 bodysmall md:bodymedium text-[var(--dispensary-gray-600)] ml-4">
                  <li>Encryption of sensitive data in transit and at rest</li>
                  <li>Regular security assessments and updates</li>
                  <li>Access controls and authentication measures</li>
                  <li>Employee training on data protection practices</li>
                </ul>
              </div>
            </div>

            {/* Your Rights */}
            <div className="md:mb-8">
              <h4 className="h4 mb-2">
                Your Rights
              </h4>
              <div className="space-y-4">
                <p className="bodysmall md:bodymedium text-[var(--dispensary-gray-600)]">
                  You have the right to:
                </p>
                <ul className="list-disc list-inside space-y-2 bodysmall md:bodymedium text-[var(--dispensary-gray-600)] ml-4">
                  <li>Access and review your personal information</li>
                  <li>Request correction of inaccurate information</li>
                  <li>Request deletion of your personal information</li>
                  <li>Opt out of marketing communications</li>
                  <li>Withdraw consent for data processing</li>
                </ul>
              </div>
            </div>

            {/* Contact Us */}
            <div className="md:mb-8">
              <h4 className="h4 mb-2">
                Contact Us
              </h4>
              <div className="space-y-4">
                <p className="bodysmall md:bodymedium text-[var(--dispensary-gray-600)]">
                  If you have any questions about this Privacy Policy or our data practices, 
                  please contact us:
                </p>
                <div className="space-y-2 bodysmall md:bodymedium text-[var(--dispensary-gray-600)]">
                  <div className="flex items-center gap-2">
                    <HugeiconsIcon icon={MailIcon} size={16} />
                    <span>Email: privacy@pharmacy.com</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <HugeiconsIcon icon={AiPhoneIcon} size={16} />
                    <span>Phone: +234 901 234 5678</span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
} 